// File: src/pages/ApprovalManager/ApprovalManagerSuddenApprovals.js
/*
 * UPDATED — AM approval is now FINAL.
 * -----------------------------------------------------------------------
 * API:
 *   GET  /api/sudden-requests/approval-manager
 *   POST /api/sudden-requests/approval-manager/{id}/approve?remark=...&isPaid=true|false
 *   POST /api/sudden-requests/approval-manager/{id}/reject?remark=...
 *
 * CHANGED: AM now decides isPaid (Paid / Unpaid) at the time of approval,
 * since AM approval is FINAL — there is no cluster manager step after this.
 * Attendance + payroll are processed immediately on approval.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  Chip, Stack, Divider, CircularProgress, Alert,
  TextField, Modal, Avatar, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { AccessTime, Person, FlashOn } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const MODAL_STYLE = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'background.paper',
  boxShadow: 24, p: 4, borderRadius: 3
};

const ApprovalManagerSuddenApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  // NEW: isPaid included in modal state, default true (Paid)
  const [modal, setModal] = useState({ open: false, id: null, approve: true, remark: '', isPaid: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sudden-requests/approval-manager');
      setRequests(res.data || []);
    } catch (e) {
      toast.error('Failed to load sudden requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (id, approve) =>
    setModal({ open: true, id, approve, remark: '', isPaid: true });

  const submit = async () => {
    if (!modal.remark.trim()) {
      toast.error('Remark is required');
      return;
    }
    setActionLoading(modal.id);
    try {
      if (modal.approve) {
        // NEW: isPaid passed here — AM's decision is final
        await api.post(
          `/sudden-requests/approval-manager/${modal.id}/approve?remark=${encodeURIComponent(modal.remark)}&isPaid=${modal.isPaid}`
        );
        toast.success('Approved (Final) — recorded and processed');
      } else {
        await api.post(
          `/sudden-requests/approval-manager/${modal.id}/reject?remark=${encodeURIComponent(modal.remark)}`
        );
        toast.success('Rejected');
      }
      setModal({ open: false, id: null, approve: true, remark: '', isPaid: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
      <CircularProgress size={56} />
    </Box>
  );

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h5" fontWeight={900} color="#1e293b">
            Sudden Request Approvals — First Level
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review emergency / late permission requests from your employees.
            Your decision here is FINAL — including Paid/Unpaid.
          </Typography>
        </Box>
        <Chip label={`${requests.length} Pending`} color="primary" sx={{ fontWeight: 800 }} />
      </Stack>

      {requests.length === 0 ? (
        <Alert severity="success" variant="outlined" sx={{ borderRadius: 3 }}>
          All sudden requests from your employees are processed.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {requests.map(req => (
            <Grid item xs={12} md={6} key={req.id}>
              <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ bgcolor: '#0f172a', width: 36, height: 36, fontSize: 14, fontWeight: 700 }}>
                        {(req.employeeName || req.employee?.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={800}>{req.employeeName || req.employee?.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{req.branchName || ''}</Typography>
                      </Box>
                    </Stack>
                    <Chip
                      label={req.requestType ? req.requestType.replace('_', ' ') : ''}
                      size="small"
                      icon={<FlashOn fontSize="small" />}
                      sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={1} mb={2}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AccessTime sx={{ fontSize: 16, color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">
                        {req.createdAt ? format(new Date(req.createdAt), 'PPp') : '-'}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, minHeight: 52 }}
                    >
                      <strong>Reason:</strong> {req.reason || '-'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth variant="contained" color="success"
                      disabled={!!actionLoading}
                      onClick={() => openModal(req.id, true)}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      Approve
                    </Button>
                    <Button
                      fullWidth variant="outlined" color="error"
                      disabled={!!actionLoading}
                      onClick={() => openModal(req.id, false)}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      Reject
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Modal open={modal.open} onClose={() => setModal({ ...modal, open: false })}>
        <Box sx={MODAL_STYLE}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            {modal.approve ? '✅ Approve Request (Final)' : '❌ Reject Request'}
          </Typography>
          <TextField
            fullWidth multiline rows={3}
            label="Your Remark *"
            value={modal.remark}
            onChange={e => setModal({ ...modal, remark: e.target.value })}
            sx={{ mb: 2 }}
          />
          {/* NEW: isPaid toggle — only shown on approve, AM's decision is final */}
          {modal.approve && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} mb={1}>
                Paid / Unpaid
              </Typography>
              <ToggleButtonGroup
                value={modal.isPaid}
                exclusive
                fullWidth
                onChange={(_, val) => { if (val !== null) setModal({ ...modal, isPaid: val }); }}
              >
                <ToggleButton value={true} color="success">Paid (No Deduction)</ToggleButton>
                <ToggleButton value={false} color="error">Unpaid (Deduction Applies)</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" fullWidth onClick={() => setModal({ ...modal, open: false })}>Cancel</Button>
            <Button variant="contained" fullWidth
              color={modal.approve ? 'success' : 'error'}
              disabled={!modal.remark.trim() || !!actionLoading}
              onClick={submit}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ApprovalManagerSuddenApprovals;